/**
 * ScheduleInspector — Phase 6b-2.
 *
 * Trigger node that fires the workflow on a cron schedule.
 * Source schema: soarNodes.js:172. Fields:
 *
 *   cron         string         cron expression (5-field)
 *   timezone     enum           tz database name (UTC default)
 *   description  string         human-readable label
 *
 * The Inspector includes a lightweight cron-syntax sanity check (5 fields,
 * no in-process cron lib needed) and surfaces the parsed-or-not status
 * inline. The full "next-fire-at" preview is deferred to Phase 6c polish
 * — it requires a cron evaluator dependency the FE doesn't ship today.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React from 'react';

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

// Common cron-shortcut recipes the operator can drop in with one click —
// covers the 5 schedules every team needs and reduces "what's the cron for
// every weekday at 9am" lookups.
const CRON_PRESETS = [
  { value: '0 * * * *',   label: 'Hourly (top of hour)' },
  { value: '0 9 * * 1-5', label: 'Weekdays at 9 AM' },
  { value: '0 0 * * *',   label: 'Daily at midnight' },
  { value: '0 0 * * 0',   label: 'Weekly (Sunday midnight)' },
  { value: '0 0 1 * *',   label: 'Monthly (1st at midnight)' },
];

export default function ScheduleInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const cron = cfg.cron || '';
  const timezone = TIMEZONES.includes(cfg.timezone) ? cfg.timezone : 'UTC';
  const description = cfg.description || '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const cronStatus = analyzeCron(cron);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Cron Expression"
        hint="5 fields: minute hour day month dayOfWeek."
      >
        <input
          type="text"
          value={cron}
          onChange={(e) => setField('cron', e.target.value)}
          placeholder="0 9 * * 1-5"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
        />
        {cron && (
          <span style={{
            fontSize: 10,
            color: cronStatus.ok ? '#065f46' : '#b91c1c',
            marginTop: 2,
          }}>
            {cronStatus.ok ? '✓ ' : '✗ '}{cronStatus.message}
          </span>
        )}
      </FieldGroup>

      {!readOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#475569',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Quick presets
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {CRON_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setField('cron', p.value)}
                title={p.value}
                style={presetBtnStyle}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <FieldGroup label="Timezone" hint="Used to evaluate the cron expression.">
        <select
          value={timezone}
          onChange={(e) => setField('timezone', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Description"
        hint="Optional. Surfaces in the schedule list + audit log."
      >
        <input
          type="text"
          value={description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="Daily security scan at 9 AM"
          disabled={readOnly}
          style={inputStyle}
        />
      </FieldGroup>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */

// Lightweight, dependency-free cron sanity check. We accept the 5-field
// classic syntax. Each field allows: digits, comma-lists, ranges (a-b),
// step (*/n), and the wildcard *. We don't try to validate semantic
// correctness (e.g. day-of-month=32) — that's the runtime's job. We
// only flag syntactic obvious-mistake cases so the operator gets early
// signal without round-tripping to the BE.
function analyzeCron(expr) {
  if (!expr || typeof expr !== 'string') return { ok: false, message: 'Empty' };
  const trimmed = expr.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) {
    return { ok: false, message: `Need 5 fields, got ${parts.length}` };
  }
  const fieldRe = /^(\*|(\d+(-\d+)?)(,\d+(-\d+)?)*)(\/\d+)?$/;
  for (let i = 0; i < 5; i++) {
    if (!fieldRe.test(parts[i])) {
      return { ok: false, message: `Field ${i + 1} invalid: "${parts[i]}"` };
    }
  }
  return { ok: true, message: 'Looks valid (server validates on save).' };
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };

const presetBtnStyle = {
  padding: '3px 8px',
  fontSize: 10.5,
  fontWeight: 500,
  color: '#0f172a',
  background: '#f1f5f9',
  border: '0.5px solid #cbd5e1',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
